/**
 * useMyInvitations hook - TanStack Query
 *
 * Constitution Section 19.1: State Management Separation
 * Constitution Section 19.2: Data Fetching Patterns
 * Constitution Section 19.4: API Response Validation (Zod Mandatory)
 *
 * This hook manages server state for user invitations using React Query.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { getMyInvitations, rejectInvitation, acceptInvitationById } from '@/api/endpoints/invitations';
import type { UserInvitationItem } from '@/schemas/invitation';

/**
 * Query key for invitations
 */
export const invitationsQueryKey = (status: 'pending' | 'all') =>
  ['invitations', 'me', status] as const;

/**
 * CRITICAL: Stable empty array reference to prevent infinite re-renders
 */
const EMPTY_INVITATIONS: UserInvitationItem[] = [];

/**
 * Hook to fetch user's invitations using TanStack Query
 *
 * Features:
 * - Automatically enabled when user is authenticated
 * - 2 minute stale time (invitations can change quickly)
 * - Zod validation at API boundary
 * - Type-safe return values
 *
 * @param status - 'pending' (default) or 'all'
 * @returns React Query result with invitations data
 */
export function useMyInvitations(status: 'pending' | 'all' = 'pending') {
  const { isAuthenticated } = useAuth0();

  const query = useQuery({
    queryKey: invitationsQueryKey(status),
    queryFn: async () => {
      return await getMyInvitations(status);
    },
    enabled: isAuthenticated,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 1,
  });

  return {
    invitations: query.data?.invitations ?? EMPTY_INVITATIONS,
    pendingCount: query.data?.pending_count ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    isError: query.isError,
    isSuccess: query.isSuccess,
  };
}

/**
 * Hook for pending invitations count (for sidebar badge)
 * Uses the same query as useMyInvitations('pending') for efficiency
 */
export function usePendingInvitationsCount() {
  const { isAuthenticated } = useAuth0();

  const query = useQuery({
    queryKey: invitationsQueryKey('pending'),
    queryFn: async () => {
      return await getMyInvitations('pending');
    },
    enabled: isAuthenticated,
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });

  return {
    count: query.data?.pending_count ?? 0,
    isLoading: query.isLoading,
  };
}

/**
 * Hook to reject an invitation
 */
export function useRejectInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: rejectInvitation,
    onSuccess: () => {
      // Invalidate both pending and all queries
      queryClient.invalidateQueries({ queryKey: ['invitations', 'me'] });
    },
  });
}

/**
 * Hook to accept an invitation by ID
 */
export function useAcceptInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: acceptInvitationById,
    onSuccess: () => {
      // Invalidate invitations queries
      queryClient.invalidateQueries({ queryKey: ['invitations', 'me'] });
      // Also invalidate companies/projects queries to reflect new membership
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

/**
 * Type-safe return types
 */
export type UseMyInvitationsReturn = ReturnType<typeof useMyInvitations>;
export type UsePendingInvitationsCountReturn = ReturnType<typeof usePendingInvitationsCount>;
export type UseRejectInvitationReturn = ReturnType<typeof useRejectInvitation>;
export type UseAcceptInvitationReturn = ReturnType<typeof useAcceptInvitation>;
